import { BLUR_RUNTIME_POLICY, READY_TO_RECORD_GATE } from '@kyc/shared';

/**
 * Pure state trackers for the ready-to-record gate (§10), the in-recording blur policy
 * (§11) and the best-frame timestamp tracker (§19). No DOM here — fully unit-testable.
 */

export interface FrameVerdict {
  timestampMs: number;
  pass: boolean;
  /** Sharpness-specific failure, driving the §11 blur clock. */
  sharp: boolean;
  /** Ranking score for §19 best-frame hints (only meaningful when pass). */
  rankScore: number;
}

// ---------------------------------------------------------------------------
// §10 — ready-to-record gate: 8 of last 10 analyzed frames pass AND the last 5
// consecutive frames pass, then a 1-second countdown.
// ---------------------------------------------------------------------------

export class ReadyGate {
  private window: boolean[] = [];
  private consecutive = 0;

  push(pass: boolean): void {
    this.window.push(pass);
    if (this.window.length > READY_TO_RECORD_GATE.windowFrames) this.window.shift();
    this.consecutive = pass ? this.consecutive + 1 : 0;
  }

  reset(): void {
    this.window = [];
    this.consecutive = 0;
  }

  get satisfied(): boolean {
    if (this.window.length < READY_TO_RECORD_GATE.windowFrames) return false;
    const passing = this.window.filter(Boolean).length;
    return (
      passing >= READY_TO_RECORD_GATE.minPassingInWindow &&
      this.consecutive >= READY_TO_RECORD_GATE.minConsecutivePassing
    );
  }

  get countdownMs(): number {
    return READY_TO_RECORD_GATE.countdownMs;
  }
}

// ---------------------------------------------------------------------------
// §11 — blur handling during recording, driven by analyzed-frame timestamps.
// ---------------------------------------------------------------------------

export type BlurAction = 'continue' | 'warn_refocus' | 'at_risk' | 'abort_take';

export class BlurPolicy {
  private blurStartMs: number | null = null;

  /** Feed each analyzed frame; returns the action the recorder must take. */
  push(timestampMs: number, sharp: boolean): BlurAction {
    if (sharp) {
      this.blurStartMs = null;
      return 'continue';
    }
    if (this.blurStartMs === null) this.blurStartMs = timestampMs;
    const blurMs = timestampMs - this.blurStartMs;
    if (blurMs > BLUR_RUNTIME_POLICY.abortAfterMs) return 'abort_take';
    if (blurMs > BLUR_RUNTIME_POLICY.warnAndRefocusMaxMs) return 'at_risk';
    if (blurMs > BLUR_RUNTIME_POLICY.transientMaxMs) return 'warn_refocus';
    return 'continue';
  }

  reset(): void {
    this.blurStartMs = null;
  }
}

// ---------------------------------------------------------------------------
// §19 — top-5 passing timestamps, ranked by client-side quality.
// ---------------------------------------------------------------------------

export class BestFrameTracker {
  private top: Array<{ timestampMs: number; score: number }> = [];

  push(v: FrameVerdict): void {
    if (!v.pass) return;
    this.top.push({ timestampMs: v.timestampMs, score: v.rankScore });
    this.top.sort((a, b) => b.score - a.score);
    if (this.top.length > 5) this.top.length = 5;
  }

  reset(): void {
    this.top = [];
  }

  /** Best-first timestamp hints for the upload manifest (§19). */
  get hintsMs(): number[] {
    return this.top.map((t) => Math.max(0, Math.round(t.timestampMs)));
  }
}

// ---------------------------------------------------------------------------
// §20 — two clocks: wall-clock recording duration and valid-quality accumulated time.
// ---------------------------------------------------------------------------

export class QualityClock {
  private validMs = 0;
  private lastTs: number | null = null;
  private lastPass = false;

  push(timestampMs: number, pass: boolean): void {
    if (this.lastTs !== null && this.lastPass && pass) {
      this.validMs += timestampMs - this.lastTs;
    }
    this.lastTs = timestampMs;
    this.lastPass = pass;
  }

  reset(): void {
    this.validMs = 0;
    this.lastTs = null;
    this.lastPass = false;
  }

  get validAccumulatedMs(): number {
    return this.validMs;
  }
}
